import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { formatErr } from "../lib/auth";
import { Shield, ArrowRight, Check } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <section className="section hero-radial min-h-[80vh] flex items-center">
        <div className="container-if max-w-md mx-auto card-dark p-9 text-center">
          <h1 className="font-display text-3xl mb-2">Invalid link.</h1>
          <p className="text-sm text-white/60 mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
            This password reset link is missing its token. Please request a new one.
          </p>
          <Link to="/forgot-password" className="btn-gold w-full">Request new link</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="section hero-radial min-h-[85vh] flex items-center">
      <div className="container-if max-w-md mx-auto">
        <div className="card-dark p-9">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-[var(--if-gold)]" strokeWidth={1.5} />
            <span className="font-mono-label text-[var(--if-gold)]">Set New Password</span>
          </div>
          {done ? (
            <>
              <div className="w-12 h-12 rounded-full bg-[var(--if-gold)]/20 flex items-center justify-center mb-5">
                <Check className="w-6 h-6 text-[var(--if-gold)]" />
              </div>
              <h1 className="font-display text-3xl mb-2">Password updated.</h1>
              <p className="text-sm text-white/60" style={{ fontFamily: "Inter, sans-serif" }}>
                Redirecting you to sign in…
              </p>
            </>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <h1 className="font-display text-3xl mb-2">Choose a new password.</h1>
              <input
                required type="password" className="if-input" placeholder="New password (6+ chars)"
                value={password} onChange={(e) => setPassword(e.target.value)} data-testid="rp-password"
              />
              <input
                required type="password" className="if-input" placeholder="Confirm password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="rp-confirm"
              />
              {err && <div className="text-red-400 text-sm">{err}</div>}
              <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="rp-submit">
                {loading ? "Updating…" : <>Update password <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
